/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { useEffect, useState } from "react";

import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import { ArrowUpRightFromSquareSolidV6, HeadsetSolidV6 } from "@shared/v2/Icomoon";
import Modal from "@shared/v2/Modal";
import TextInput from "@shared/v2/TextInput";

import { useIsLever, useTheme } from "../../reducers/layoutReducer/selectors";
import useRequestConnectServiceDeactivation from "./useRequestConnectServiceDeactivation";

// The frame hardcodes "your PLACE partnership", which is wrong for the other two brands.
// ConnectServicesActivationModal solves this with a useBrand hook, but that module also imports
// three hero JPGs — far too much to pull in for a single word — so the name is resolved here.
const useBrandName = () => {
  const isLever = useIsLever();
  const isPlaceTheme = useTheme()?.name === "place-theme";

  if (isLever) return "Lever";
  return isPlaceTheme ? "PLACE" : "Brivity";
};

// No reason enum exists anywhere yet — the deactivation endpoint takes free text — so this list is
// authored from the flow's intent and is the one thing here a stakeholder should sanity-check.
const DEACTIVATION_REASONS = [
  "My clients aren't using it",
  "My clients had a poor experience",
  "The service isn't available in my market",
  "It's too difficult to use during a transaction",
  "I'd like to use a different provider",
  "I'm not seeing enough value for my clients",
].map((reason) => ({ label: reason, value: reason }));

const ARTICLES = [
  {
    title: "Learn about Connect Services",
    description: "How Connect works and what to expect",
    href: "https://www.brivityknowledge.com/docs/connect-services-your-client-s-move-in-partner",
  },
  {
    title: "Learn about using Connect Services in a Transaction",
    description: "Full step-by-step process for using Connect Services",
    href: "https://www.brivityknowledge.com/docs/use-connect-services-in-a-transaction",
  },
];

const CARD_CLASSES =
  "tw-flex tw-w-full tw-gap-[16px] tw-rounded-[8px] tw-border tw-border-solid tw-bg-white tw-p-[16px]";

// Both form controls are 4px in the design; the shared controls default to 2px. `isPipeline` is the
// dropdown's own switch for the 4px variant — it is read off the rest props rather than declared
// (Dropdown.jsx), so it cannot be passed type-safely, hence the cast on the whole prop set.
const DROPDOWN_RADIUS_PROPS = { isPipeline: true } as Record<string, unknown>;

interface ConnectServicesSupportModalProps {
  show?: boolean;
  onClose?: () => void;
}

const ConnectServicesSupportModal = ({
  show = false,
  onClose = () => {},
}: ConnectServicesSupportModalProps) => {
  const brandName = useBrandName();
  const [reason, setReason] = useState<(typeof DEACTIVATION_REASONS)[number] | null>(null);
  const [additionalFeedback, setAdditionalFeedback] = useState("");
  const { requestCallback, error, isSubmitting, resetError } = useRequestConnectServiceDeactivation();

  // Reset on open rather than on close: the v2 Modal keeps its children mounted through a 400ms
  // exit animation, so clearing on close would visibly blank the form while it fades out.
  useEffect(() => {
    if (show) {
      setReason(null);
      setAdditionalFeedback("");
      resetError();
    }
  }, [show, resetError]);

  // Nothing is deactivated here — this only asks the Connect Services team to call. The modal
  // closes on success and the integration card stays in its "Get support" state, because the
  // account keeps the service until their team acts on the ticket.
  const handleRequestCallback = async () => {
    if (await requestCallback(reason.value, additionalFeedback)) onClose();
  };

  return (
    <Modal
      show={show}
      onHide={onClose}
      dialogClassName="tw-flex tw-min-h-full tw-max-h-full tw-items-center tw-justify-center"
      // The width and padding overrides need `!`: Modal appends its own size and `tw-p-[32px]`
      // after contentClassName, so an unflagged utility loses the cascade.
      contentClassName="tw-m-auto tw-flex tw-max-h-[682px] tw-flex-col tw-gap-[32px] tw-overflow-hidden !tw-w-[645px] !tw-px-[24px] !tw-py-[32px] !tw-shadow-[0px_1px_12px_5px_rgba(51,51,51,0.25)]"
    >
      {/* @ts-expect-error props */}
      <Modal.Header title="Connect Services Support" closeButton />

      <Modal.Body className="tw-relative tw-flex tw-min-h-0 tw-flex-1 tw-flex-col tw-items-start tw-gap-[24px] tw-overflow-y-auto tw-pl-[8px] tw-pr-[16px]">
        <div className="tw-text-14d tw-text-neutral-gray-75">
          <span className="tw-block tw-font-bold">
            Having trouble or not getting the results you expected?
          </span>
          We want to make sure you&apos;re getting full value from this part of your {brandName}{" "}
          partnership. Explore the resources below or reach out directly.
        </div>

        <div className="tw-flex tw-w-full tw-flex-col tw-gap-[8px]">
          {ARTICLES.map((article) => (
            <div key={article.title} className={`${CARD_CLASSES} tw-h-[68px] tw-items-center tw-border-neutral-gray-10`}>
              <div className="tw-flex tw-min-w-px tw-flex-1 tw-flex-col tw-items-start tw-text-12d">
                <span className="tw-font-semibold tw-text-neutral-gray-75">{article.title}</span>
                <span className="tw-text-neutral-gray-50">{article.description}</span>
              </div>
              {/* @ts-expect-error props */}
              <Button
                size="small"
                schema="tertiary"
                className="tw-flex tw-items-center tw-gap-[4px]"
                onClick={() => window.open(article.href, "_blank")}
              >
                Read article
                <ArrowUpRightFromSquareSolidV6 size="m" />
              </Button>
            </div>
          ))}

          <div className={`${CARD_CLASSES} tw-flex-col tw-items-start tw-border-white tw-shadow-card`}>
            <span className="tw-text-12d tw-font-semibold tw-text-neutral-gray-75">
              Still need help? Reach out directly.
            </span>
            <div className="tw-flex tw-items-center tw-gap-[8px]">
              <HeadsetSolidV6 size="l" className="tw-text-neutral-gray-75" />
              <div className="tw-flex tw-flex-col tw-text-12d">
                <span className="tw-text-neutral-gray-75">Connect Services team</span>
                <div className="tw-flex tw-items-center tw-gap-[6px] tw-font-semibold tw-text-neutral-gray-75">
                  <span className="tw-border-y-0 tw-border-l-[1px] tw-border-r-0 tw-border-solid tw-border-neutral-gray-15 tw-pl-[6px]">
                    (844) 406-1540
                  </span>
                  <span className="tw-border-y-0 tw-border-l-[1px] tw-border-r-0 tw-border-solid tw-border-neutral-gray-15 tw-pl-[6px] tw-text-theme-text-link-color tw-underline">
                    info@connectservices.com
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <hr className="tw-my-0 tw-h-0 tw-w-full tw-border-x-0 tw-border-b-0 tw-border-t-[1px] tw-border-solid tw-border-neutral-gray-10" />

        <div className="tw-text-14d tw-text-neutral-gray-75">
          <span className="tw-block tw-font-bold">
            No longer interested in using Connect Services?
          </span>
          Let us know why and a team member will give you a call to understand your experience and help
          with next steps.
        </div>

        <Dropdown
          // @ts-expect-error props
          label="Reason for Deactivating"
          isRequired
          name="reason"
          placeholder="Please select a reason"
          helperText="To help us improve, please share why you're no longer interested in Connect Services"
          options={DEACTIVATION_REASONS}
          value={reason}
          onChange={setReason}
          menuShouldScrollIntoView
          className="tw-w-full"
          containerClassName="tw-w-full"
          {...DROPDOWN_RADIUS_PROPS}
        />

        <TextInput
          // @ts-expect-error props
          label="Additional Feedback"
          name="additionalFeedback"
          multiline
          value={additionalFeedback}
          onChange={(event) => setAdditionalFeedback(event.target.value)}
          containerClassName="tw-w-full"
          className="tw-h-[116px] tw-resize-none !tw-rounded-[4px] !tw-px-[12px] !tw-py-[8px]"
        />
      </Modal.Body>

      {/* ModalFooter ships no classes of its own, so the whole layout comes from here. */}
      <Modal.Footer
        data-cy="modal-footer"
        className="tw-flex tw-items-center tw-justify-between tw-gap-[16px] tw-px-[8px]"
      >
        {/* @ts-expect-error props */}
        <Button size="medium" schema="tertiary" onClick={onClose}>
          Close
        </Button>
        {error && (
          <span
            data-cy="connect-services-deactivation-error"
            role="alert"
            className="tw-ml-auto tw-text-right tw-text-14d tw-font-normal tw-text-[color:var(--semantic-red-100)]"
          >
            {error}
          </span>
        )}
        {/* @ts-expect-error props */}
        <Button
          size="medium"
          schema="warning"
          disabled={!reason}
          isLoading={isSubmitting}
          onClick={handleRequestCallback}
        >
          Request a Callback
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConnectServicesSupportModal;
